<script lang="ts">
  /**
   * ImportStep — orquestra o fluxo de importação de arquivos.
   *
   * Estados:
   *   - empty: sem arquivos carregados
   *   - loading: lendo arquivos (estado transitório no FileDropzone)
   *   - loaded: arquivos carregados, exibe FileSummary + ValidationSummary
   *   - blocked: há issues blocking, alerta visual, mas não impede exibição
   *
   * Nunca exibe conteúdo bruto nem PII do arquivo.
   */
  import { appStore } from '../../app/store';
  import { validateLocalFileBasics } from '../../core/files/fileValidation';
  import type { LocalInputFile } from '../../core/files/file.types';
  import type { ValidationSummary as ValidationSummaryType, ValidationIssue } from '../../core/validation/validation.types';
  import FileDropzone from './FileDropzone.svelte';
  import FileSummary from './FileSummary.svelte';
  import ValidationSummaryComponent from '../validation/ValidationSummary.svelte';
  import EmptyState from '../../components/EmptyState.svelte';

  const { state } = appStore;

  $: privacyAccepted = $state.privacyAccepted;
  $: loadedFiles = $state.loadedFiles;

  let validationSummary: ValidationSummaryType | null = null;

  function buildValidationSummary(issues: ValidationIssue[]): ValidationSummaryType {
    const blockingCount = issues.filter((i) => i.severity === 'blocking').length;
    const errorCount = issues.filter((i) => i.severity === 'error').length;
    const warningCount = issues.filter((i) => i.severity === 'warning').length;
    const infoCount = issues.filter((i) => i.severity === 'info').length;
    const hasBlocking = blockingCount > 0;
    let confidence: ValidationSummaryType['confidence'];
    if (hasBlocking || errorCount > 0) {
      confidence = 'low';
    } else if (warningCount > 0) {
      confidence = 'medium';
    } else {
      confidence = 'high';
    }
    return {
      issues,
      blockingCount,
      errorCount,
      warningCount,
      infoCount,
      hasBlocking,
      confidence,
    };
  }

  function handleFilesSelected(files: LocalInputFile[]): void {
    appStore.addLoadedFiles(files);
    const allIssues: ValidationIssue[] = files.flatMap((f) => validateLocalFileBasics(f));
    if (allIssues.length > 0) {
      validationSummary = buildValidationSummary(allIssues);
    } else {
      // Clear previous validation if all new files are valid
      validationSummary = null;
    }
  }
</script>

<section class="import-step" aria-label="Importação de arquivos">
  <div class="import-step__dropzone">
    <FileDropzone
      onFilesSelected={handleFilesSelected}
      disabled={!privacyAccepted}
    />
  </div>

  {#if loadedFiles.length === 0 && privacyAccepted}
    <div class="import-step__empty">
      <EmptyState
        message="Nenhum arquivo carregado"
        description="Arraste arquivos AFD para a área acima ou clique para selecionar."
      />
    </div>
  {/if}

  {#if loadedFiles.length > 0}
    <div class="import-step__summary">
      <FileSummary files={loadedFiles} />
    </div>
  {/if}

  {#if validationSummary !== null}
    <div class="import-step__validation">
      <ValidationSummaryComponent validation={validationSummary} />
    </div>
  {/if}
</section>

<style>
  .import-step {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-5);
  }

  .import-step__empty {
    border: 1px solid var(--ptt-gray-20);
    border-radius: var(--ptt-radius-md);
    background: var(--ptt-white);
  }


</style>
